import { REALTIME_EVENTS, RealtimeSocket, SocketEventHandler } from "./types";

/**
 * Compatibilidade incremental com o legado:
 * - o `chatbot-web` continua chamando `.on/.off/.disconnect`;
 * - a conexao usa o namespace `/realtime/organization-{organizationId}`;
 * - a autenticacao usa o token do Void Auth em `?token=` (config/socket.ts).
 *
 * O backend Go hoje emite SSE no formato:
 *   event: channel:new-message
 *   data: {"event":"channel:new-message","payload":{...}}
 *
 * Este client tambem tolera mensagem generica em `onmessage` com `data.event`.
 */
export class SseSocket implements RealtimeSocket {
  public disconnected = true;

  private readonly listeners = new Map<string, Set<SocketEventHandler>>();
  private readonly nativeListeners = new Map<string, EventListener>();
  private readonly eventSource: EventSource;

  constructor(url: string) {
    this.eventSource = new EventSource( url, { withCredentials: true });
    this.disconnected = false;

    this.eventSource.onopen = () => {
      this.disconnected = false;
      console.log("Conectado ao realtime SSE");
    };

    this.eventSource.onerror = ( error ) => {
      const readyState = this.eventSource.readyState;
      const stateLabel = this.describeReadyState( readyState );

      if ( readyState === EventSource.OPEN ) {
        this.disconnected = false;
        console.warn(`Evento SSE error recebido com conexao ainda aberta (${ stateLabel }).`, error );
        return;
      }

      if ( readyState === EventSource.CONNECTING ) {
        this.disconnected = false;
        console.warn(`Realtime SSE em reconexao automatica (${ stateLabel }).`, error );
        return;
      }

      if ( readyState === EventSource.CLOSED ) {
        this.disconnected = true;
        console.error(`Realtime SSE encerrado (${ stateLabel }).`, error );
        return;
      }

      console.error(`Realtime SSE em estado desconhecido (${ stateLabel }).`, error );
    };

    this.eventSource.onmessage = ( rawEvent ) => {
      const parsed = this.parseMessage( rawEvent.data );
      if ( !parsed?.event ) return;

      this.dispatch( parsed.event, parsed.payload );
    };

    REALTIME_EVENTS.forEach(( eventName ) => {
      const listener = (( rawEvent: Event ) => {
        const messageEvent = rawEvent as MessageEvent<string>;
        const parsed = this.parseMessage( messageEvent.data, eventName );

        this.dispatch( parsed.event, parsed.payload );
      }) as EventListener;

      this.nativeListeners.set( eventName, listener );
      this.eventSource.addEventListener( eventName, listener );
    });
  }

  get connected() {
    return !this.disconnected;
  }

  on(event: string, handler: SocketEventHandler): RealtimeSocket {
    const eventName = event.trim();
    if ( !eventName ) return this;

    let handlers = this.listeners.get( eventName );
    if ( !handlers ) {
      handlers = new Set<SocketEventHandler>();
      this.listeners.set( eventName, handlers );
    }

    handlers.add( handler );
    return this;
  }

  off(event: string, handler?: SocketEventHandler): RealtimeSocket {
    const eventName = event.trim();
    const handlers = this.listeners.get( eventName );

    if ( !handlers ) return this;

    if ( handler ) handlers.delete( handler );
    else handlers.clear();

    if ( handlers.size === 0 ) this.listeners.delete( eventName );

    return this;
  }

  emit(event: string, _payload?: any): RealtimeSocket {
    console.warn(`Realtime SSE nao suporta emit client->server. Evento ignorado: ${ event }`);
    return this;
  }

  disconnect() {
    this.nativeListeners.forEach(( listener, eventName ) => {
      this.eventSource.removeEventListener( eventName, listener );
    });

    this.nativeListeners.clear();
    this.listeners.clear();
    this.eventSource.close();
    this.disconnected = true;
  }

  private parseMessage(rawData: string, fallbackEvent?: string) {
    try {
      const envelope = JSON.parse( rawData );
      const eventName = typeof envelope?.event === "string" && envelope.event.trim()
        ? envelope.event.trim()
        : fallbackEvent ?? "message";

      return {
        event: eventName,
        payload: envelope?.payload ?? envelope,
      };
    } catch ( error ) {
      console.error(`Falha ao processar evento realtime ${ fallbackEvent ?? "message" }:`, error );

      return {
        event: fallbackEvent ?? "message",
        payload: rawData,
      };
    }
  }

  private dispatch(eventName: string, payload: any) {
    const handlers = this.listeners.get( eventName );
    if ( !handlers ) return;

    handlers.forEach(( handler ) => handler( payload ));
  }

  private describeReadyState(readyState: number) {
    switch ( readyState ) {
      case EventSource.CONNECTING:
        return "CONNECTING";
      case EventSource.OPEN:
        return "OPEN";
      case EventSource.CLOSED:
        return "CLOSED";
      default:
        return `UNKNOWN:${ readyState }`;
    }
  }
}
